import { LOCATION_CHANGE } from 'connected-react-router';
import { call, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import { FeatureLimitedWebInfo } from 'src/reducers/appReducer';
import { v4 as uuid } from 'uuid';
import { getToken } from '../../../selectors/authSelectors';
import {
  getFeatureLimitedWeb,
  getPolicyListApiUrl,
} from '../../../selectors/configSelectors';
import { filterByRegex, mergeRegexFilters } from '../../../utils';
import { isLifeOnlyInsuredPolicy, isOwnerPolicy } from '../../../utils/common';
import { myAxaFetchWithToken } from '../../../utils/myAxaFetchWithToken';
import {
  FETCH_PROLICY_LIST,
  FETCH_PROLICY_LIST_FEATURES_FAILURE,
  FETCH_PROLICY_LIST_SUCCESS,
  RESET_PROLICY_LIST,
} from '../actions';
import {
  getFilterPolicyListByRegex,
  getPolicyHiddenRidersList,
} from '../selectors/policyListSelectors';
import { sortPolicies } from '@axa-asia/utils';

export function* fetchPolicyListFeatures(action): Iterable<any> {
  do {
    try {
      const featureName = action?.payload?.featureName;
      const getRouter = (state: any) => state.router.location.pathname;
      const pathname = yield select(getRouter);
      const hiddenRiders = yield select(getPolicyHiddenRidersList);
      const featureLimitedWeb: FeatureLimitedWebInfo = yield select(
        getFeatureLimitedWeb,
      );

      if (pathname.indexOf('/policy-list') > -1 || featureName) {
        let isbetterMeFlag = false;
        let isAxaGoalFlag = false;
        const navToList = pathname.split('/');
        let navTo = featureName ?? navToList[3];
        isbetterMeFlag =
          navToList.length > 4 && navToList[4] == 'betterme' ? true : false;
        if (navTo === 'viewleaflet') {
          navTo = 'mypolicy';
          isAxaGoalFlag = true;
        }
        const url =
          (yield select(getPolicyListApiUrl)) + `?featureName=${navTo}`;
        const token = select(getToken);
        if (token) {
          const res = yield call(myAxaFetchWithToken, url, {
            method: 'GET',
            headers: {
              'x-axahk-msgid': uuid(),
            },
          });
          // const res = { status: 200, json: () => require('../mock/feature.json') }
          const policyListApiResponseBody: any[] = yield res.json();
          let regex = yield select(getFilterPolicyListByRegex);

          let policies = policyListApiResponseBody;

          // HKEMMA-123260 [Web eCard ] PN is missing in PL page & HKEMMA-136482 enable eCard all EB
          // for EB web portal case navTo=healthcard, for mixcared portal navTo=mycards
          if (navTo === 'mycards' || navTo === 'healthcard') {
            // HKEMMA-149065 merge regex instead of combined to prevent duplicate policy
            regex = mergeRegexFilters(regex, {
              entityCode: '^(EHK)',
            });
          }

          policies = filterByRegex(policies, regex, 'policy'); // This will only used by Web, don't need to check isApp()

          policies = isbetterMeFlag
            ? policies.filter(
                (item: { policy: { betterMeFlag: string } }) =>
                  item.policy.betterMeFlag == 'Y',
              )
            : policies;
          policies = isAxaGoalFlag
            ? policies.filter(
                (item: { policy: { axaGoalPolicy: string } }) =>
                  item.policy.axaGoalPolicy === 'Y',
              )
            : policies;
          const returnPolicies: any = [];
          policies.forEach((item: any) => {
            const itemPolicy = {
              ...item,
              isLifeOnlyInsuredPolicy: isLifeOnlyInsuredPolicy(item.policy),
              isOwnerPolicy: isOwnerPolicy(item.policy),
            };
            returnPolicies.push(itemPolicy);
          });

          const ebPolicyNumber = policyListApiResponseBody.map(
            (p) => p.policy.policyNumber,
          );

          const ebPolicies = featureLimitedWeb.ebPolicies.filter((p) =>
            ebPolicyNumber.includes(p.policyNumber),
          );

          if (featureLimitedWeb.ebAllowedWebFeatures.includes(featureName)) {
            returnPolicies.push(...ebPolicies);
          }

          if (hiddenRiders) {
            returnPolicies.forEach((item: any) => {
              if (!item || !item.policyRiders || !item.policyRiders.length)
                return;
              item.policyRiders = item.policyRiders.filter((rider: any) => {
                if (!rider || !Object.keys(rider).length || !rider.planCode) {
                  return true;
                } else {
                  return !hiddenRiders.includes(rider.planCode);
                }
              });
            });
          }

          // Group life saving & investment policy
          returnPolicies.map((item: any) => {
            if (
              ['SAVINGS_PRODUCT', 'UNIT_LINK_PRODUCT'].includes(
                item.policy?.policyCategory,
              )
            ) {
              item.policy.policyCategory = 'GROUP_SAVINGS_UNIT_LINK_PRODUCT';
            }
            return item;
          });

          const sortedPolicies = sortPolicies(returnPolicies); // HKEMMA-160193

          yield put({
            type: FETCH_PROLICY_LIST_SUCCESS,
            policies: sortedPolicies,
          });
          yield action?.payload?.callback?.(sortedPolicies);
        }
      }
    } catch (e) {
      yield put({ type: FETCH_PROLICY_LIST_FEATURES_FAILURE, error: e });
    }
    while (true) {
      const action = yield take(LOCATION_CHANGE);
      if (action.payload.location.pathname.indexOf('/policy-list') > -1) {
        yield put({ type: RESET_PROLICY_LIST });
        break;
      }
    }
  } while (true);
}

function* fetchPolicyList(): Iterable<any> {
  yield call(fetchPolicyListFeatures, null);
}

function* resetPolicyList(): any {
  yield put({ type: RESET_PROLICY_LIST });
}

function* polictListSaga() {
  // yield take(FRAME_APP_MOUNTED)
  yield fork(resetPolicyList);
  yield fork(fetchPolicyList);
  yield takeLatest(FETCH_PROLICY_LIST, fetchPolicyListFeatures);
}

export default polictListSaga;
